import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import type { PriceListItem } from '../types';

/* ─────────────────────────────────────────────────────────────────────────
 * Item Code Builder — Web port of VBA Form Castors (CastorWheelsCoding.xlsm)
 *
 * Replicates the 13-component pricing engine:
 *   Castor → Wheel → BB → Circlip → Spacer → Bush → TG → NB → CI → FC → Adp → Axle → FB
 *
 * The item code is built as:
 *   {CastorType}/{WheelType}/{WheelSize}/{BB}[/CIR|Spacer]/{Bush}/{TG}/{NB}[/CI]...[/FB]
 * ───────────────────────────────────────────────────────────────────────── */

// Castor types (from VBA Master sheet)
const CASTOR_TYPES = [
  { code: 'SW', label: 'Swivel (SW)', desc: '360° rotation, standard' },
  { code: 'FX', label: 'Fixed (FX)', desc: 'Rigid fixed direction' },
  { code: 'SWB', label: 'Swivel Brake (SWB)', desc: '360° with foot brake' },
];

// Wheel types (from VBA codingMaster)
const WHEEL_TYPES = [
  { code: 'PP/R/S/SPLAST', label: 'Polypropylene Solid', material: 'PP' },
  { code: 'NY/PL', label: 'Nylon', material: 'NY' },
  { code: 'PUCI/PL', label: 'Polyurethane CI', material: 'PU' },
  { code: 'PU', label: 'Polyurethane', material: 'PU' },
  { code: 'UHM/B', label: 'UHMWPE', material: 'UHM' },
  { code: 'CI/PL', label: 'Cast Iron', material: 'CI' },
  { code: 'RUB/PL', label: 'Rubber', material: 'RUB' },
  { code: 'NY/R/S', label: 'Nylon Resilient', material: 'NYR' },
];

// Wheel sizes (common)
const WHEEL_SIZES = [
  { code: '80 x 25', inch: '3 x 1' },
  { code: '80 x 70', inch: '3 x 2 3/4' },
  { code: '100 x 32', inch: '4 x 1 1/4' },
  { code: '100 x 32 Light', inch: '4 x 1 1/4 Light' },
  { code: '100 x 38', inch: '4 x 1 1/2' },
  { code: '100 x 45', inch: '4 x 1 3/4' },
  { code: '100 x 50', inch: '4 x 2' },
  { code: '100 x 75', inch: '4 x 3' },
  { code: '125 x 38', inch: '5 x 1 1/2' },
  { code: '125 x 45', inch: '5 x 1 3/4' },
  { code: '125 x 50', inch: '5 x 2' },
  { code: '150 x 38', inch: '6 x 1 1/2' },
  { code: '150 x 38 Light', inch: '6 x 1 1/2 Light' },
  { code: '150 x 45', inch: '6 x 1 3/4' },
  { code: '150 x 50', inch: '6 x 2' },
  { code: '150 x 75', inch: '6 x 3' },
  { code: '175 x 40', inch: '7 x 1 1/2' },
  { code: '175 x 50', inch: '7 x 2' },
  { code: '200 x 45', inch: '8 x 1 3/4' },
  { code: '200 x 50', inch: '8 x 2' },
  { code: '200 x 62', inch: '8 x 2 1/2' },
  { code: '250 x 62', inch: '10 x 2 1/2' },
];

// Bearing types
const BEARING_TYPES = [
  { code: '6201', label: '6201', price: 40 },
  { code: '6202', label: '6202', price: 45 },
  { code: '6203', label: '6203', price: 70 },
  { code: '6204', label: '6204', price: 91 },
  { code: '6205', label: '6205', price: 103 },
  { code: '6206', label: '6206', price: 156 },
  { code: '6305', label: '6305', price: 203 },
];

// Thread guard types
const TG_TYPES = [
  { code: 'PTG', label: 'PTG (Plain Thread Guard)', value: 1 },
  { code: 'ETG', label: 'ETG (Extended Thread Guard)', value: 2 },
  { code: 'MTG', label: 'MTG (Metal Thread Guard)', value: 3 },
  { code: 'LTG', label: 'LTG (Long Thread Guard)', value: 4 },
];

// Nut bolt types
const NB_TYPES = [
  { code: 'NB', label: 'NB (Standard Nut Bolt)' },
  { code: 'SSNB', label: 'SSNB (SS Nut Bolt)' },
];

// Bush types
const BUSH_TYPES = [
  { code: 'GB', label: 'GB (GF Bush)', desc: 'General purpose' },
  { code: 'PB', label: 'PB (Pipe Bush)', desc: 'Pipe fitting' },
  { code: 'SSB', label: 'SSB (SS Bush)', desc: 'Stainless steel' },
];

// CI types (Circlip Inner)
const CI_TYPES = [
  { code: 'HCI', label: 'HCI (Heavy CI)' },
  { code: 'MCIH', label: 'MCIH (Medium CI Heavy)' },
];

interface ItemCodeBuilderProps {
  onAddItem: (itemCode: string, description: string, rate: number) => void;
  onClose?: () => void;
}

export default function ItemCodeBuilder({ onAddItem, onClose }: ItemCodeBuilderProps) {
  const [priceList, setPriceList] = useState<PriceListItem[]>([]);
  const [loading, setLoading] = useState(true);

  // Component selections
  const [castorType, setCastorType] = useState('');
  const [wheelType, setWheelType] = useState('');
  const [wheelSize, setWheelSize] = useState('');
  const [enableBB, setEnableBB] = useState(false);
  const [bearingType, setBearingType] = useState('');
  const [bearingQty, setBearingQty] = useState(1);
  const [enableCirclip, setEnableCirclip] = useState(false);
  const [enableSpacer, setEnableSpacer] = useState(false);
  const [enableBush, setEnableBush] = useState(false);
  const [bushType, setBushType] = useState('');
  const [enableTG, setEnableTG] = useState(false);
  const [tgType, setTgType] = useState('');
  const [enableNB, setEnableNB] = useState(false);
  const [nbType, setNbType] = useState('');
  const [enableCI, setEnableCI] = useState(false);
  const [ciType, setCiType] = useState('');
  const [enableFlange, setEnableFlange] = useState(false);
  const [enableAdapter, setEnableAdapter] = useState(false);
  const [enableAxle, setEnableAxle] = useState(false);
  const [enableFootBrake, setEnableFootBrake] = useState(false);

  // Price override
  const [manualRate, setManualRate] = useState(0);

  useEffect(() => {
    loadPriceList();
  }, []);

  async function loadPriceList() {
    const { data } = await supabase.from('price_list').select('*');
    if (data) setPriceList(data);
    setLoading(false);
  }

  // Build item code from selections (replicates VBA ItemCode())
  const buildItemCode = useCallback(() => {
    const parts: string[] = [];

    // Castor type + size
    if (castorType) {
      parts.push(castorType);
    }

    // Wheel type + size
    if (wheelType) {
      parts.push(wheelType);
    }

    if (wheelSize) {
      parts.push(wheelSize);
    }

    // Bearing
    if (enableBB && bearingType) {
      parts.push(`BB${bearingType}`);
    }

    // Circlip or Spacer
    if (enableCirclip && bearingType) {
      parts.push('CIR');
    } else if (enableSpacer) {
      parts.push('Spacer');
    }

    // Bush
    if (enableBush && bushType) {
      parts.push(bushType);
    }

    // Thread guard
    if (enableTG && tgType) {
      parts.push(tgType);
    }

    // Nut bolt
    if (enableNB && nbType) {
      parts.push(nbType);
    }

    // CI
    if (enableCI && ciType) {
      parts.push(ciType);
    }

    // Flange
    if (enableFlange) {
      parts.push('FC');
    }

    // Adapter
    if (enableAdapter) {
      parts.push('Adp');
    }

    // Axle
    if (enableAxle) {
      parts.push('Axle');
    }

    // Foot brake (only for SWB)
    if (enableFootBrake && castorType === 'SWB') {
      parts.push('FB');
    }

    const code = parts.join('/');
    // Clean double slashes
    return code.replace(/\/+/g, '/').replace(/\/$/, '');
  }, [
    castorType, wheelType, wheelSize, enableBB, bearingType, bearingQty,
    enableCirclip, enableSpacer, enableBush, bushType, enableTG, tgType,
    enableNB, nbType, enableCI, ciType, enableFlange, enableAdapter,
    enableAxle, enableFootBrake,
  ]);

  // Build description
  const buildDescription = useCallback(() => {
    const parts: string[] = [];
    if (castorType) parts.push(CASTOR_TYPES.find(c => c.code === castorType)?.label || castorType);
    if (wheelType) parts.push(WHEEL_TYPES.find(w => w.code === wheelType)?.label || wheelType);
    if (wheelSize) parts.push(wheelSize);
    if (enableBB) parts.push(`Bearing ${bearingType}${bearingQty > 1 ? ` x${bearingQty}` : ''}`);
    if (enableCirclip) parts.push('Circlip');
    if (enableSpacer) parts.push('Spacer');
    if (enableBush) parts.push(BUSH_TYPES.find(b => b.code === bushType)?.label || bushType);
    if (enableTG) parts.push(TG_TYPES.find(t => t.code === tgType)?.label || tgType);
    if (enableNB) parts.push(NB_TYPES.find(n => n.code === nbType)?.label || nbType);
    if (enableCI) parts.push(CI_TYPES.find(c => c.code === ciType)?.label || ciType);
    if (enableFlange) parts.push('Flange');
    if (enableAdapter) parts.push('Adapter');
    if (enableAxle) parts.push('Axle');
    if (enableFootBrake) parts.push('Foot Brake');
    return parts.join(' + ');
  }, [
    castorType, wheelType, wheelSize, enableBB, bearingType, bearingQty,
    enableCirclip, enableSpacer, enableBush, bushType, enableTG, tgType,
    enableNB, nbType, enableCI, ciType, enableFlange, enableAdapter,
    enableAxle, enableFootBrake,
  ]);

  // Calculate component prices
  const calculatePrices = useCallback(() => {
    const prices: Record<string, number> = {};

    // BB price
    if (enableBB && bearingType) {
      const bb = BEARING_TYPES.find(b => b.code === bearingType);
      prices.bb = (bb?.price || 0) * bearingQty;
    }

    // TB price (from price list)
    if (castorType && wheelSize) {
      const castorItem = priceList.find(p =>
        p.category === 'Castors' &&
        (p.item_code || '').toUpperCase().includes(castorType)
      );
      prices.castor = castorItem?.price || 0;
    }

    if (wheelType && wheelSize) {
      const wheelItem = priceList.find(p =>
        p.category === 'Wheels' &&
        (p.item_code || '').toUpperCase().includes(wheelType.split('/')[0])
      );
      prices.wheel = wheelItem?.price || 0;
    }

    // CIR price
    if (enableCirclip && bearingType) {
      const cirItem = priceList.find(p =>
        p.category === 'CIR' &&
        (p.item_code || '').includes(bearingType)
      );
      prices.cir = cirItem?.price || 0;
    }

    // TG price
    if (enableTG && tgType) {
      const tgItem = priceList.find(p =>
        p.category === 'TG' && (p.item_code || '').includes('TG')
      );
      prices.tg = tgItem?.price || 0;
    }

    // NB price
    if (enableNB && nbType) {
      const nbItem = priceList.find(p =>
        p.category === 'NB' && (p.item_code || '').includes('NB')
      );
      prices.nb = nbItem?.price || 0;
    }

    // GB (bush) price
    if (enableBush && bushType) {
      const bushItem = priceList.find(p =>
        p.category === 'GB' && (p.item_code || '').includes(bushType)
      );
      prices.bush = bushItem?.price || 0;
    }

    const total = Object.values(prices).reduce((sum, p) => sum + p, 0);

    return { prices, total: Math.ceil(total) };
  }, [
    enableBB, bearingType, bearingQty, castorType, wheelType, wheelSize,
    enableCirclip, enableTG, tgType, enableNB, nbType, enableBush, bushType,
    priceList,
  ]);

  const itemCode = buildItemCode();
  const description = buildDescription();
  const { prices: componentPrices, total: calculatedTotal } = calculatePrices();
  const rate = manualRate > 0 ? manualRate : calculatedTotal;

  const isValid = castorType && wheelType && wheelSize;

  function handleAdd() {
    if (!itemCode) return;
    onAddItem(itemCode, description || itemCode, rate);
    // Reset
    setCastorType('');
    setWheelType('');
    setWheelSize('');
    setEnableBB(false);
    setBearingType('');
    setEnableCirclip(false);
    setEnableSpacer(false);
    setEnableBush(false);
    setBushType('');
    setEnableTG(false);
    setTgType('');
    setEnableNB(false);
    setNbType('');
    setEnableCI(false);
    setCiType('');
    setEnableFlange(false);
    setEnableAdapter(false);
    setEnableAxle(false);
    setEnableFootBrake(false);
    setManualRate(0);
  }

  function resetAll() {
    setCastorType('');
    setWheelType('');
    setWheelSize('');
    setEnableBB(false);
    setBearingType('');
    setBearingQty(1);
    setEnableCirclip(false);
    setEnableSpacer(false);
    setEnableBush(false);
    setBushType('');
    setEnableTG(false);
    setTgType('');
    setEnableNB(false);
    setNbType('');
    setEnableCI(false);
    setCiType('');
    setEnableFlange(false);
    setEnableAdapter(false);
    setEnableAxle(false);
    setEnableFootBrake(false);
    setManualRate(0);
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-blue-600 border-t-transparent mr-3" />
        <span className="text-sm text-slate-500">Loading component data...</span>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-2xl shadow-card border border-slate-200/80 overflow-hidden">
      {/* Header */}
      <div className="bg-gradient-to-r from-slate-900 to-blue-900 px-6 py-4">
        <div className="flex justify-between items-center">
          <div>
            <h2 className="text-lg font-black text-white flex items-center gap-2">
              ⚙️ Item Code Builder
            </h2>
            <p className="text-xs text-blue-300 mt-0.5">
              VBA Form Castors — 13 Component Pricing Engine
            </p>
          </div>
          {onClose && (
            <button onClick={onClose} className="text-white/60 hover:text-white text-xl cursor-pointer">✕</button>
          )}
        </div>
      </div>

      <div className="p-6 space-y-6">
        {/* Generated Code Preview */}
        <div className={`p-4 rounded-xl border-2 transition-all ${itemCode ? 'border-blue-500 bg-blue-50' : 'border-slate-200 bg-slate-50'}`}>
          <div className="flex items-center justify-between mb-2">
            <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Generated Item Code</span>
            {itemCode && (
              <button onClick={resetAll} className="text-[10px] font-bold text-red-500 hover:text-red-700 cursor-pointer">
                Reset All
              </button>
            )}
          </div>
          <div className={`font-mono text-lg font-black ${itemCode ? 'text-blue-900' : 'text-slate-400'}`}>
            {itemCode || 'Select components below...'}
          </div>
          {description && (
            <p className="text-xs text-slate-600 mt-1">{description}</p>
          )}
          {itemCode && (
            <div className="flex items-center gap-4 mt-3 pt-3 border-t border-blue-200">
              <div className="text-sm font-black text-blue-700">
                ₹{rate.toLocaleString('en-IN')}
              </div>
              <div className="text-[10px] text-slate-500">per piece</div>
              {Object.keys(componentPrices).length > 0 && (
                <div className="flex flex-wrap gap-1 ml-auto">
                  {Object.entries(componentPrices).map(([key, val]) => (
                    val > 0 && (
                      <span key={key} className="text-[9px] px-1.5 py-0.5 bg-white border border-blue-200 rounded text-blue-700 font-bold">
                        {key.toUpperCase()}: ₹{val}
                      </span>
                    )
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* ─── STEP 1: Castor Type ─── */}
        <div className="space-y-3">
          <StepHeader step={1} title="Castor Type" active={!castorType} />
          <div className="grid grid-cols-3 gap-3">
            {CASTOR_TYPES.map(ct => (
              <button
                key={ct.code}
                onClick={() => {
                  setCastorType(ct.code);
                  if (ct.code !== 'SWB') setEnableFootBrake(false);
                }}
                className={`p-4 rounded-xl border-2 text-left transition-all cursor-pointer ${
                  castorType === ct.code
                    ? 'border-blue-500 bg-blue-50 shadow-md'
                    : 'border-slate-200 hover:border-blue-300 hover:bg-slate-50'
                }`}
              >
                <div className="font-mono font-black text-sm text-slate-900">{ct.code}</div>
                <div className="text-xs font-bold text-slate-700 mt-1">{ct.label}</div>
                <div className="text-[10px] text-slate-500 mt-0.5">{ct.desc}</div>
              </button>
            ))}
          </div>
        </div>

        {/* ─── STEP 2: Wheel ─── */}
        {castorType && (
          <div className="space-y-3 animate-fadeIn">
            <StepHeader step={2} title="Wheel Selection" active={!wheelType || !wheelSize} />

            {/* Wheel Type */}
            <div>
              <label className="block text-[10px] font-bold text-slate-600 mb-2 uppercase">Wheel Material</label>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                {WHEEL_TYPES.map(wt => (
                  <button
                    key={wt.code}
                    onClick={() => setWheelType(wt.code)}
                    className={`p-3 rounded-xl border-2 text-left transition-all cursor-pointer ${
                      wheelType === wt.code
                        ? 'border-blue-500 bg-blue-50 shadow-sm'
                        : 'border-slate-200 hover:border-blue-300'
                    }`}
                  >
                    <div className="text-xs font-bold text-slate-900">{wt.label}</div>
                    <div className="font-mono text-[10px] text-slate-500">{wt.code}</div>
                  </button>
                ))}
              </div>
            </div>

            {/* Wheel Size */}
            {wheelType && (
              <div className="animate-fadeIn">
                <label className="block text-[10px] font-bold text-slate-600 mb-2 uppercase">Wheel Size (mm)</label>
                <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-6 gap-2 max-h-48 overflow-y-auto">
                  {WHEEL_SIZES.map(ws => (
                    <button
                      key={ws.code}
                      onClick={() => setWheelSize(ws.code)}
                      className={`p-2 rounded-lg border text-left transition-all cursor-pointer ${
                        wheelSize === ws.code
                          ? 'border-blue-500 bg-blue-50 shadow-sm'
                          : 'border-slate-200 hover:border-blue-300'
                      }`}
                    >
                      <div className="font-mono text-xs font-bold text-slate-900">{ws.code}</div>
                      <div className="text-[10px] text-slate-500">{ws.inch}</div>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* ─── STEP 3: Components ─── */}
        {wheelSize && (
          <div className="space-y-4 animate-fadeIn">
            <StepHeader step={3} title="Component Assembly" active={false} />

            {/* Ball Bearing */}
            <ComponentToggle
              label="🔩 Ball Bearing (BB)"
              enabled={enableBB}
              onToggle={() => {
                setEnableBB(!enableBB);
                if (!enableBB) { setBearingType('6203'); }
                else { setBearingType(''); setEnableCirclip(false); }
              }}
            >
              {enableBB && (
                <div className="space-y-2 mt-2">
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                    {BEARING_TYPES.map(bt => (
                      <button
                        key={bt.code}
                        onClick={() => { setBearingType(bt.code); setEnableCirclip(false); }}
                        className={`p-2 rounded-lg border text-center transition-all cursor-pointer ${
                          bearingType === bt.code
                            ? 'border-blue-500 bg-blue-50'
                            : 'border-slate-200 hover:border-blue-300'
                        }`}
                      >
                        <div className="font-mono text-xs font-bold">{bt.code}</div>
                        <div className="text-[10px] text-slate-500">₹{bt.price}</div>
                      </button>
                    ))}
                  </div>
                  {bearingType && (
                    <div className="flex items-center gap-3 mt-2 p-2 bg-slate-50 rounded-lg">
                      <label className="text-[10px] font-bold text-slate-600">Qty:</label>
                      <div className="flex gap-1">
                        {[1, 2].map(q => (
                          <button
                            key={q}
                            onClick={() => setBearingQty(q)}
                            className={`px-3 py-1 rounded text-xs font-bold cursor-pointer ${
                              bearingQty === q ? 'bg-blue-600 text-white' : 'bg-white border border-slate-300'
                            }`}
                          >{q}</button>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Circlip / Spacer */}
                  {bearingType && (
                    <div className="mt-2 p-3 bg-slate-50 rounded-xl border border-slate-200">
                      <label className="text-[10px] font-bold text-slate-600 mb-2 block uppercase">Circlip or Spacer</label>
                      <div className="flex gap-2">
                        <button
                          onClick={() => { setEnableCirclip(!enableCirclip); setEnableSpacer(false); }}
                          className={`flex-1 p-2 rounded-lg border text-xs font-bold text-center transition-all cursor-pointer ${
                            enableCirclip
                              ? 'border-blue-500 bg-blue-50 text-blue-700'
                              : 'border-slate-200 hover:border-blue-300'
                          }`}
                        >
                          CIR
                        </button>
                        <button
                          onClick={() => { setEnableSpacer(!enableSpacer); setEnableCirclip(false); }}
                          className={`flex-1 p-2 rounded-lg border text-xs font-bold text-center transition-all cursor-pointer ${
                            enableSpacer
                              ? 'border-blue-500 bg-blue-50 text-blue-700'
                              : 'border-slate-200 hover:border-blue-300'
                          }`}
                        >
                          Spacer
                        </button>
                        <button
                          onClick={() => { setEnableCirclip(false); setEnableSpacer(false); }}
                          className={`flex-1 p-2 rounded-lg border text-xs font-bold text-center transition-all cursor-pointer ${
                            !enableCirclip && !enableSpacer
                              ? 'border-slate-400 bg-slate-200 text-slate-600'
                              : 'border-slate-200 hover:border-slate-400'
                          }`}
                        >
                          NIL
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </ComponentToggle>

            {/* Bush */}
            <ComponentToggle
              label="🔧 Bush (GB/PB/SSB)"
              enabled={enableBush}
              onToggle={() => {
                setEnableBush(!enableBush);
                if (!enableBush) setBushType('GB');
                else setBushType('');
              }}
            >
              {enableBush && (
                <div className="grid grid-cols-3 gap-2 mt-2">
                  {BUSH_TYPES.map(bt => (
                    <button
                      key={bt.code}
                      onClick={() => setBushType(bt.code)}
                      className={`p-2 rounded-lg border text-left transition-all cursor-pointer ${
                        bushType === bt.code
                          ? 'border-blue-500 bg-blue-50'
                          : 'border-slate-200 hover:border-blue-300'
                      }`}
                    >
                      <div className="text-xs font-bold">{bt.label}</div>
                      <div className="text-[10px] text-slate-500">{bt.desc}</div>
                    </button>
                  ))}
                </div>
              )}
            </ComponentToggle>

            {/* Thread Guard */}
            <ComponentToggle
              label="🛡️ Thread Guard (TG/ETG/MTG/LTG)"
              enabled={enableTG}
              onToggle={() => {
                setEnableTG(!enableTG);
                if (!enableTG) setTgType('PTG');
                else setTgType('');
              }}
            >
              {enableTG && (
                <div className="grid grid-cols-2 gap-2 mt-2">
                  {TG_TYPES.map(t => (
                    <button
                      key={t.code}
                      onClick={() => setTgType(t.code)}
                      className={`p-2 rounded-lg border text-left transition-all cursor-pointer ${
                        tgType === t.code
                          ? 'border-blue-500 bg-blue-50'
                          : 'border-slate-200 hover:border-blue-300'
                      }`}
                    >
                      <div className="font-mono text-xs font-bold">{t.code}</div>
                      <div className="text-[10px] text-slate-500">{t.label}</div>
                    </button>
                  ))}
                </div>
              )}
            </ComponentToggle>

            {/* Nut Bolt */}
            <ComponentToggle
              label="🔩 Nut Bolt (NB/SSNB)"
              enabled={enableNB}
              onToggle={() => {
                setEnableNB(!enableNB);
                if (!enableNB) setNbType('NB');
                else setNbType('');
              }}
            >
              {enableNB && (
                <div className="grid grid-cols-2 gap-2 mt-2">
                  {NB_TYPES.map(n => (
                    <button
                      key={n.code}
                      onClick={() => setNbType(n.code)}
                      className={`p-2 rounded-lg border text-left transition-all cursor-pointer ${
                        nbType === n.code
                          ? 'border-blue-500 bg-blue-50'
                          : 'border-slate-200 hover:border-blue-300'
                      }`}
                    >
                      <div className="font-mono text-xs font-bold">{n.code}</div>
                      <div className="text-[10px] text-slate-500">{n.label}</div>
                    </button>
                  ))}
                </div>
              )}
            </ComponentToggle>

            {/* CI (Circlip Inner) */}
            <ComponentToggle
              label="⭕ CI (Inner Circlip)"
              enabled={enableCI}
              onToggle={() => {
                setEnableCI(!enableCI);
                if (!enableCI) setCiType('HCI');
                else setCiType('');
              }}
            >
              {enableCI && (
                <div className="grid grid-cols-2 gap-2 mt-2">
                  {CI_TYPES.map(c => (
                    <button
                      key={c.code}
                      onClick={() => setCiType(c.code)}
                      className={`p-2 rounded-lg border text-left transition-all cursor-pointer ${
                        ciType === c.code
                          ? 'border-blue-500 bg-blue-50'
                          : 'border-slate-200 hover:border-blue-300'
                      }`}
                    >
                      <div className="font-mono text-xs font-bold">{c.code}</div>
                      <div className="text-[10px] text-slate-500">{c.label}</div>
                    </button>
                  ))}
                </div>
              )}
            </ComponentToggle>

            {/* Optional toggles row */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              <SmallToggle label="Flange (FC)" enabled={enableFlange} onToggle={() => setEnableFlange(!enableFlange)} />
              <SmallToggle label="Adapter (Adp)" enabled={enableAdapter} onToggle={() => setEnableAdapter(!enableAdapter)} />
              <SmallToggle label="Axle" enabled={enableAxle} onToggle={() => setEnableAxle(!enableAxle)} />
              {castorType === 'SWB' && (
                <SmallToggle label="Foot Brake (FB)" enabled={enableFootBrake} onToggle={() => setEnableFootBrake(!enableFootBrake)} />
              )}
            </div>
          </div>
        )}

        {/* ─── STEP 4: Rate & Add ─── */}
        {itemCode && (
          <div className="space-y-3 animate-fadeIn pt-4 border-t border-slate-200">
            <StepHeader step={4} title="Set Rate & Add to Quotation" active={false} />

            <div className="flex items-end gap-4">
              <div className="flex-1">
                <label className="block text-[10px] font-bold text-slate-600 mb-1 uppercase">Rate per Piece (₹)</label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={manualRate || ''}
                  onChange={(e) => setManualRate(parseFloat(e.target.value) || 0)}
                  placeholder={`Calculated: ₹${calculatedTotal}`}
                  className="block w-full px-4 py-3 border-2 border-slate-300 rounded-xl text-lg font-black font-mono focus:border-blue-500"
                />
                <p className="text-[10px] text-slate-500 mt-1">
                  {manualRate > 0 ? 'Manual rate set' : `Auto-calculated from components: ₹${calculatedTotal.toLocaleString('en-IN')}`}
                </p>
              </div>
              <button
                onClick={handleAdd}
                disabled={!isValid}
                className="px-8 py-3 bg-blue-600 hover:bg-blue-700 text-white font-black text-sm rounded-xl shadow-lg shadow-blue-600/30 disabled:opacity-40 disabled:cursor-not-allowed transition-all cursor-pointer whitespace-nowrap"
              >
                + Add to Quote
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/* ─── Helper Components ─── */

function StepHeader({ step, title, active }: { step: number; title: string; active: boolean }) {
  return (
    <div className="flex items-center gap-3">
      <div className={`h-7 w-7 rounded-full flex items-center justify-center text-xs font-black ${
        active ? 'bg-blue-100 text-blue-700 animate-pulse' : 'bg-blue-600 text-white'
      }`}>
        {step}
      </div>
      <h3 className={`text-sm font-bold ${active ? 'text-blue-700' : 'text-slate-900'}`}>{title}</h3>
    </div>
  );
}

function ComponentToggle({
  label,
  enabled,
  onToggle,
  children,
}: {
  label: string;
  enabled: boolean;
  onToggle: () => void;
  children?: React.ReactNode;
}) {
  return (
    <div className={`rounded-xl border-2 transition-all ${enabled ? 'border-blue-200 bg-blue-50/50' : 'border-slate-200 bg-white'}`}>
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between p-3 cursor-pointer"
      >
        <span className="text-sm font-bold text-slate-800">{label}</span>
        <div className={`w-10 h-5 rounded-full transition-colors relative ${enabled ? 'bg-blue-600' : 'bg-slate-300'}`}>
          <div className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-all ${enabled ? 'left-5' : 'left-0.5'}`} />
        </div>
      </button>
      {enabled && children}
    </div>
  );
}

function SmallToggle({ label, enabled, onToggle }: { label: string; enabled: boolean; onToggle: () => void }) {
  return (
    <button
      onClick={onToggle}
      className={`p-2 rounded-xl border-2 text-xs font-bold text-center transition-all cursor-pointer ${
        enabled
          ? 'border-blue-500 bg-blue-50 text-blue-700'
          : 'border-slate-200 hover:border-blue-300 text-slate-600'
      }`}
    >
      {label}
    </button>
  );
}
